import React, { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { LayoutGrid, GripVertical } from 'lucide-react';

function SortableWidgetItem({ widget, info, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.widget_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-secondary/30 rounded-lg ${isDragging ? 'ring-2 ring-primary' : ''}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-secondary rounded"
      >
        <GripVertical size={16} className="text-muted-foreground" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium">{info.name}</p>
        <p className="text-xs text-muted-foreground">{info.description}</p>
      </div>
      <Switch
        checked={widget.enabled}
        onCheckedChange={onToggle}
        disabled={info.locked}
      />
    </div>
  );
}

export function WidgetSettingsDialog({ availableWidgets, preferences, onSave, open, onOpenChange }) {
  const { t } = useI18n();
  const [localPrefs, setLocalPrefs] = useState(preferences);

  useEffect(() => {
    setLocalPrefs(preferences);
  }, [preferences]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleWidget = (widgetId) => {
    setLocalPrefs(prev => ({
      ...prev,
      widgets: prev.widgets.map(w => 
        w.widget_id === widgetId ? { ...w, enabled: !w.enabled } : w
      )
    }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      setLocalPrefs(prev => {
        const oldIndex = prev.widgets.findIndex(w => w.widget_id === active.id);
        const newIndex = prev.widgets.findIndex(w => w.widget_id === over.id);
        
        const newWidgets = arrayMove(prev.widgets, oldIndex, newIndex).map((w, idx) => ({
          ...w,
          position: idx
        }));
        
        return { ...prev, widgets: newWidgets };
      });
    }
  };

  const handleSave = () => {
    onSave(localPrefs);
    onOpenChange(false);
  };

  const getWidgetInfo = (widgetId) => {
    return availableWidgets.find(w => w.id === widgetId) || {};
  };

  const sortedWidgets = [...(localPrefs.widgets || [])].sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid size={20} />
            {t('dashboard.widgetSettings')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.widgetSettingsDesc')}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 max-h-[60vh] overflow-y-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedWidgets.map(w => w.widget_id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {sortedWidgets.map(widget => {
                  const info = getWidgetInfo(widget.widget_id);
                  return (
                    <SortableWidgetItem
                      key={widget.widget_id}
                      widget={widget}
                      info={info}
                      onToggle={() => toggleWidget(widget.widget_id)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave}>{t('common.save')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
